import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", company: "" });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return undefined; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState({ status: "paid", company: data.company });
        if (["expired", "failed"].includes(data.payment_status)) return setState({ status: "failed" });
      } catch { /* keep polling */ }
      if (++attempts >= 8) return setState({ status: "timeout" });
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-5 py-20 text-center" data-testid="payment-success-page">
      {state.status === "checking" && (<><Loader2 size={40} className="mx-auto text-emerald-600 animate-spin mb-4" /><h1 className="font-display text-3xl text-slate-900" style={{ fontWeight: 800 }}>Confirming your payment…</h1><p className="text-slate-500 mt-2">This usually takes a few seconds.</p></>)}
      {state.status === "paid" && (
        <div data-testid="payment-paid">
          <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-4" />
          <h1 className="font-display text-4xl text-slate-900" style={{ fontWeight: 900 }}>You're claimed.</h1>
          <p className="text-slate-600 mt-3">{state.company} now shows a verified employer badge. A receipt is on its way to your billing email.</p>
          <Button asChild className="mt-6 rounded-full bg-slate-900 hover:bg-emerald-600"><Link to={`/company/${encodeURIComponent(state.company || "")}`} data-testid="payment-view-company">View your company page</Link></Button>
        </div>
      )}
      {state.status === "timeout" && (<><Loader2 size={40} className="mx-auto text-amber-500 mb-4" /><h1 className="font-display text-3xl text-slate-900" style={{ fontWeight: 800 }}>Still processing</h1><p className="text-slate-500 mt-2">Your payment is being confirmed. Check your email for the receipt, or refresh in a minute.</p></>)}
      {(state.status === "failed" || state.status === "error") && (<><XCircle size={44} className="mx-auto text-rose-500 mb-4" /><h1 className="font-display text-3xl text-slate-900" style={{ fontWeight: 800 }}>Payment not completed</h1><p className="text-slate-500 mt-2">No charge was made. You can try again from the pricing page.</p><Button asChild variant="outline" className="mt-6 rounded-full"><Link to="/pricing">Back to pricing</Link></Button></>)}
    </div>
  );
}
